import { useEffect, useRef, useState } from "react";
import { Scale, Dog, Cat, Rabbit, Power, RotateCcw, ArrowRightLeft } from "lucide-react";

const SpeciesIcon = ({ species, className }) => {
  if (species === "cat") return <Cat className={className} />;
  if (species === "rabbit") return <Rabbit className={className} />;
  return <Dog className={className} />;
};

const KG_TO_LB = 2.20462;

// Interactive walk-on scale. The learner powers it on, tares it, optionally
// switches kg/lb, then places the patient on the platform — the readout ramps
// up from 0 to the true weight with a little settling wobble before it locks.
export function WeighScale({ target = 0, species = "dog" }) {
  const [power, setPower] = useState(false);
  const [unit, setUnit] = useState("kg");
  const [onScale, setOnScale] = useState(false);
  const [val, setVal] = useState(0); // raw kg currently on the platform
  const [tareOffset, setTareOffset] = useState(0);
  const [settled, setSettled] = useState(false);
  const raf = useRef(0);
  const valRef = useRef(0);

  useEffect(() => () => cancelAnimationFrame(raf.current), []);

  const setValue = (v) => {
    valRef.current = v;
    setVal(v);
  };

  const animateTo = (toKg, lock) => {
    cancelAnimationFrame(raf.current);
    setSettled(false);
    const from = valRef.current;
    const start = performance.now();
    const dur = 1500;
    const step = (now) => {
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      const base = from + (toKg - from) * eased;
      const wobble = toKg > 0 && t > 0.8 ? Math.sin((t - 0.8) * 40) * (1 - t) * toKg * 0.08 : 0;
      setValue(Math.max(0, base + wobble));
      if (t < 1) raf.current = requestAnimationFrame(step);
      else {
        setValue(toKg);
        if (lock) setSettled(true);
      }
    };
    raf.current = requestAnimationFrame(step);
  };

  const togglePower = () => {
    cancelAnimationFrame(raf.current);
    if (power) {
      setPower(false);
      setOnScale(false);
      setSettled(false);
      setTareOffset(0);
      setValue(0);
    } else {
      setPower(true);
      setOnScale(false);
      setSettled(false);
      setTareOffset(0);
      setValue(0);
    }
  };

  const togglePatient = () => {
    if (!power) return;
    if (onScale) {
      setOnScale(false);
      animateTo(0, false);
    } else {
      setOnScale(true);
      animateTo(target, true);
    }
  };

  const tare = () => {
    if (!power) return;
    cancelAnimationFrame(raf.current);
    setTareOffset(valRef.current); // zero the current reading
    setSettled(onScale ? false : settled);
  };

  const toggleUnit = () => {
    if (!power) return;
    setUnit((u) => (u === "kg" ? "lb" : "kg"));
  };

  const displayedKg = Math.max(0, val - tareOffset);
  const shown = unit === "kg" ? displayedKg : displayedKg * KG_TO_LB;
  const status = !power
    ? "OFF"
    : settled
    ? "● STABLE"
    : onScale
    ? "WEIGHING…"
    : tareOffset > 0
    ? "TARED · READY"
    : "READY";

  const btn =
    "flex items-center justify-center gap-1.5 text-xs font-semibold rounded-md px-3 py-2 border transition-colors disabled:opacity-40 disabled:cursor-not-allowed";

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="weigh-scale">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
        <Scale className="w-3.5 h-3.5" /> Walk-on scale
      </div>

      <div className="flex items-center gap-4">
        {/* digital readout */}
        <div
          className={`rounded-md bg-[#0a0a0a] border-2 px-4 py-2 min-w-[140px] ${
            settled ? "border-emerald-500" : power ? "border-[#333]" : "border-[#1a1a1a]"
          }`}
        >
          <div
            className={`font-mono-clinical font-bold leading-none tabular-nums whitespace-nowrap ${
              power && onScale && !settled ? "alarm-pulse" : ""
            }`}
            style={{
              color: !power ? "#1f2937" : settled ? "#39FF14" : "#FFEA00",
              fontSize: "2rem",
            }}
            data-testid="weigh-scale-readout"
          >
            {power ? shown.toFixed(1) : "––––"}
            <span className="text-sm text-slate-500"> {unit}</span>
          </div>
          <div className="text-[9px] font-mono-clinical text-slate-500 tracking-widest text-right">
            {status}
          </div>
        </div>

        {/* platform */}
        <div className="flex-1">
          <div className="relative h-12 rounded-md bg-slate-100 border border-slate-300 flex items-end justify-center overflow-hidden">
            <SpeciesIcon
              species={species}
              className={`w-7 h-7 text-slate-500 mb-1.5 transition-all duration-500 ${
                onScale ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              }`}
            />
            <div
              className="absolute bottom-0 left-0 right-0 h-1.5 transition-colors"
              style={{ background: settled ? "#10b981" : power ? "#cbd5e1" : "#e2e8f0" }}
            />
          </div>
          <div className="text-[10px] text-slate-400 mt-1 text-center">
            {!power
              ? "Scale is off — power it on to begin"
              : settled
              ? "Reading locked — record it below"
              : onScale
              ? "Patient settling on the platform…"
              : "Tare if needed, then place the patient"}
          </div>
        </div>
      </div>

      {/* controls */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
        <button
          type="button"
          onClick={togglePower}
          data-testid="scale-power-btn"
          className={`${btn} ${
            power
              ? "border-emerald-500 bg-emerald-50 text-emerald-700"
              : "border-slate-300 text-slate-600 hover:border-slate-400"
          }`}
        >
          <Power className="w-3.5 h-3.5" /> {power ? "On" : "Off"}
        </button>
        <button
          type="button"
          onClick={tare}
          disabled={!power}
          data-testid="scale-tare-btn"
          className={`${btn} border-slate-300 text-slate-600 hover:border-slate-400`}
        >
          <RotateCcw className="w-3.5 h-3.5" /> Tare
        </button>
        <button
          type="button"
          onClick={toggleUnit}
          disabled={!power}
          data-testid="scale-unit-btn"
          className={`${btn} border-slate-300 text-slate-600 hover:border-slate-400`}
        >
          <ArrowRightLeft className="w-3.5 h-3.5" /> {unit === "kg" ? "→ lb" : "→ kg"}
        </button>
        <button
          type="button"
          onClick={togglePatient}
          disabled={!power}
          data-testid="scale-place-btn"
          className={`${btn} ${
            onScale
              ? "border-[#0047AB] bg-[#0047AB]/5 text-[#0047AB]"
              : "border-[#0047AB] bg-[#0047AB] text-white hover:bg-[#0a3f8f]"
          }`}
        >
          <SpeciesIcon species={species} className="w-3.5 h-3.5" />
          {onScale ? "Step off" : "Place patient"}
        </button>
      </div>
    </div>
  );
}
